/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { useCallback, useEffect, useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import {
  getEditBOQItemsTabs,
  fetchTakeoffsListAttachedToItem,
  getMeasurementUnits,
  saveItemInEstimate,
  isEstimateRequiredPermission,
} from "@Actions/estimation";
import { Box, Tab, Tabs } from "@mui/material";
import BoqItemBasicDetails from "./BoqItemBasicDetails";
import BoqItemQuantityDetails from "./BoqItemQuantityDetails";
import type { TakeoffSelectionRow } from "./BoqItemQuantityDetails";
import BoqItemRateDetails from "./BoqItemRateDetails";
import BoqItemUnitDetails from "./BoqItemUnitDetails";
import BoqItemAmountDetails from "./BoqItemAmountDetails";
import type { RateAnalysisData, RateAnalysisPayload, RateAnalysisRow } from "./RateAnalysis";
import _ from "lodash";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { ADDON_CODE_TO_LAYOUT_FLAG, folderPermissions, RATE_ANALYSIS_ADDON_CODE } from "@Helpers/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useParams } from "react-router-dom";
import useLayoutModuleAccess from "@Services/useLayoutModuleAccess";
import type { RootState } from "src/reducers";
import type {
  EstimateCurrency,
  EstimateData as EstimateDataState,
  GetEstimateItemsRequestValue,
} from "@ServerTypes/estimates/estimates.schema";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

type CurrencyDetails = EstimateCurrency;

/**
 * `rateAnalysis` as it arrives ON THE ROW. The backend stores it as `z.unknown()`
 * (`saveItemDetailsRequest.rateAnalysis`, legacy array/object format) and the BOQ grid asserts its
 * row into `BoqItemDetails`, so neither the row list nor the purchase-unit count is guaranteed to
 * be there. `normalizedRateAnalysis` below fills those two holes once; everything downstream —
 * component state and the payloads the tabs send back — uses the complete `RateAnalysisPayload`.
 */
type StoredRateAnalysisPayload = {
  rateAnalysisData: Omit<RateAnalysisData, "data"> & { data?: RateAnalysisRow[] };
  purchaseUnitCount?: number;
};

type ItemCurrentDetails = {
  code?: string;
  description?: string;
  purchaseUnit?: ObjectIdString;
  purchaseUnitName?: string;
  measurementUnit?: ObjectIdString;
  measurementUnitName?: string;
  conversionRatePU?: string;
  conversionRateMU?: string;
  /**
   * BUG, kept visible on purpose: `validateItemDetails` below still tests `rate === null`, which the
   * pre-TS version of this file produced for an emptied box (`parseFloat("")` -> NaN -> null). The TS
   * migration (77d68e3097) reads `Number(target.value)` instead — `Number("")` is `0`, not NaN — and
   * initialises both fields to `undefined`, so `null` never reaches state and those two rate checks
   * are dead: an emptied Rate box saves as `0` instead of being rejected. Restoring them means
   * declaring these `number | null` (the payload schema already takes `rate`/`quantity` nullable) and
   * widening the same two props in BoqItemBasicDetails / BoqItemQuantityDetails / BoqItemRateDetails,
   * which re-declare this shape.
   */
  manualQuantity?: number;
  quantityFromTakeoffs?: number;
  rate?: number;
  amount?: number;
  additionalAmount?: number | string;
  hasChild?: boolean;
  itemId?: ObjectIdString;
  rateAnalysis: RateAnalysisPayload;
  isRateAnalysisEnable?: boolean;
};

type BoqItemDetails = {
  code?: string;
  description?: string;
  purchaseUnit?: { id?: ObjectIdString; name?: string };
  measurementUnit?: { id?: ObjectIdString; name?: string; relation?: string };
  manualQuantity?: number;
  quantityFromTakeoffs?: number;
  rate?: number | null;
  amount?: number;
  additionalAmount?: number | string;
  hasChild?: boolean;
  itemId?: ObjectIdString;
  rateAnalysis?: StoredRateAnalysisPayload;
  isRateAnalysisEnable?: boolean;
};

type BoqItemEditPopUpProps = {
  closeEditModalPopUp: () => void;
  itemDetails: BoqItemDetails;
  currencyDetails: CurrencyDetails;
  refreshCallBack: (data: BoqItemDetails) => void;
};

/** One row of the getMeasurementUnits list, exactly as `units.schema.ts` declares it. */
type MeasurementUnitListRow = NonNullable<
  import("@ServerTypes/units/units.schema").GetMeasurementUnitsResponse["result"]
>[number];

// ADDED: derived from server schema GetMeasurementUnitsResponse.result item; the API
// also returns `nameWithSymbol` and `defaultMeasurementUnit*` projected fields used in
// the picker. The base shape already covers `_id`, `type`, `selectedSymbol`.
type MeasurementUnitOption = MeasurementUnitListRow & {
  _id: string;
  nameWithSymbol: string;
  type: { name: string; id: string };
  /**
   * Projected from `defaultMeasurementUnit._id`; declared `objectIdString` everywhere it appears
   * in units.schema.ts (purchase-unit request/response and getUnitTypes), so it is branded here
   * rather than re-declared as a plain `string`.
   */
  defaultMeasurementUnitId?: ObjectIdString;
  defaultMeasurementUnit?: string;
  defaultConversionRate?: string;
};

/**
 * One node of the UNIT tab's tree picker: a measurement-unit TYPE grouping its units. `_id` is the
 * unit type's id (`MeasurementUnitOption["type"]["id"]`, an `objectIdString` in units.schema.ts), so
 * it must not be re-declared as a plain `string` — `BoqItemUnitDetails` takes branded `_id`s.
 */
type MeasurementUnitTypeGroup = {
  _id: MeasurementUnitOption["type"]["id"];
  name: string;
  selectable: boolean;
  units: Array<{ _id: string; name: string; type: MeasurementUnitOption["type"] }>;
};

/**
 * The list aggregation computes `nameWithSymbol` and projects `_id` and `type`, but `units.schema.ts`
 * declares all three optional, so the rows are CHECKED here rather than asserted: `refactorUnitsData`
 * groups by `type.name` and keys each tree node by `type.id`, and would build a `undefined`-named
 * group from a row that is missing them.
 */
const isMeasurementUnitOption = (unit: MeasurementUnitListRow): unit is MeasurementUnitOption =>
  unit._id !== undefined &&
  unit.nameWithSymbol !== undefined &&
  unit.type !== undefined &&
  unit.type.id !== undefined &&
  unit.type.name !== undefined;

/**
 * `/updateEstimateItem` is declared with `getEstimateItemsResponse`, whose `result` is an ARRAY,
 * but the service assigns the SINGLE updated item (`output.result = quantityCalculationForItem(...)`
 * in estimates.service.ts). Check what actually arrives instead of asserting the declared type away;
 * the response schema is the real fix.
 */
const isSavedBoqItem = (result: unknown): result is BoqItemDetails =>
  typeof result === "object" && result !== null && !Array.isArray(result);

/**
 * Tab order of the edit modal, in the order `getEditBOQItemsTabs()` declares them. Listed
 * explicitly because `Object.entries()` widens the key back to `string`, which then has to be
 * asserted back into the tab-key union at the point the `<Tab>` list is built.
 */
const EDIT_BOQ_TAB_KEYS = ["DETAILS", "QUANTITY", "UNIT", "RATE", "AMOUNT"] as const;

const BoqItemEditPopUp = ({
  closeEditModalPopUp,
  itemDetails,
  currencyDetails,
  refreshCallBack,
}: BoqItemEditPopUpProps) => {
  const { t } = useTranslation();
  const EditBOQItemsTabs = getEditBOQItemsTabs() as {
    [k in "DETAILS" | "QUANTITY" | "UNIT" | "RATE" | "AMOUNT"]: { value: string; text?: string };
  };
  const [buttonLoading, setButtonLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(EditBOQItemsTabs.DETAILS.value);
  const [attachedTakeoffsList, setAttachedTakeoffsList] = useState<TakeoffSelectionRow[]>([]);
  const [measurementUnitsDataSource, setMeasurementUnitsDataSource] = useState<MeasurementUnitTypeGroup[]>([]);
  const normalizedRateAnalysis: RateAnalysisPayload = itemDetails.rateAnalysis
    ? {
        ...itemDetails.rateAnalysis,
        rateAnalysisData: {
          ...itemDetails.rateAnalysis.rateAnalysisData,
          data: (itemDetails.rateAnalysis.rateAnalysisData.data ?? []).map((row: RateAnalysisRow) => ({
            ...row,
            id: row.id ?? null,
          })),
        },
        purchaseUnitCount: itemDetails.rateAnalysis.purchaseUnitCount ?? 1,
      }
    : { rateAnalysisData: { data: [], total: 0, say: 0 }, purchaseUnitCount: 1 };
  const [itemCurrentDetails, setItemCurrentDetails] = useState<ItemCurrentDetails>({
    code: itemDetails.code,
    description: itemDetails.description,
    purchaseUnit: !itemDetails.hasChild ? _.get(itemDetails, "purchaseUnit.id") : undefined,
    purchaseUnitName: !itemDetails.hasChild ? _.get(itemDetails, "purchaseUnit.name") : "",
    measurementUnit: !itemDetails.hasChild ? _.get(itemDetails, "measurementUnit.id") : undefined,
    measurementUnitName: !itemDetails.hasChild ? _.get(itemDetails, "measurementUnit.name") : "",
    conversionRatePU: itemDetails.measurementUnit?.relation?.split(":")[0],
    conversionRateMU: itemDetails.measurementUnit?.relation?.split(":")[1],
    manualQuantity: itemDetails.manualQuantity ?? undefined,
    quantityFromTakeoffs: itemDetails.quantityFromTakeoffs ?? undefined,
    rate: !itemDetails.hasChild ? (itemDetails.rate ?? undefined) : undefined,
    amount: itemDetails.amount,
    additionalAmount: itemDetails.additionalAmount,
    hasChild: itemDetails.hasChild,
    itemId: itemDetails.itemId,
    rateAnalysis: normalizedRateAnalysis,
    isRateAnalysisEnable: !itemDetails.hasChild ? itemDetails.isRateAnalysisEnable : false,
  });

  const addonsAccess = useLayoutModuleAccess(ADDON_CODE_TO_LAYOUT_FLAG[RATE_ANALYSIS_ADDON_CODE]);

  const { RESOURCE_MODIFY } = folderPermissions();
  // Route params are the external entry point for these ids; the backend route schema validates them.
  const params = useParams<{ estimateId: ObjectIdString; versionId: ObjectIdString }>();

  const estimateData = useSelector((store: RootState) => store.estimateData) as EstimateDataState;
  const dispatch = useDispatch();
  const [isActionPermission] = useState(isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id));

  const validateItemDetails = () => {
    let content;
    const manualQuantity = itemCurrentDetails.manualQuantity ?? 0;
    const rateValue = itemCurrentDetails.rate ?? 0;
    const conversionRateMU = Number(itemCurrentDetails.conversionRateMU);
    const conversionRatePU = Number(itemCurrentDetails.conversionRatePU);
    const additionalAmount = itemCurrentDetails.additionalAmount ?? 0;

    if (manualQuantity < 0) {
      content = t("Please enter a positive value for the quantity");
      return { status: false, content };
    }

    if (Number(additionalAmount) < 0) {
      content = t("Please enter a positive value for the Additional Amount");
      return { status: false, content };
    }

    // The two `rate === null` tests below are statically dead — see the BUG note on
    // `ItemCurrentDetails.rate`: state never holds `null` since the TS migration. Reviving them means
    // widening `rate` to `number | null` here AND in BoqItemBasicDetails.tsx / BoqItemRateDetails.tsx,
    // which re-declare this shape, so they are kept visible rather than deleted.
    if (
      !itemCurrentDetails.code &&
      manualQuantity &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- dead by design; see the note above.
      (!itemCurrentDetails.purchaseUnit || itemCurrentDetails.rate === null)
    ) {
      content = t("Please specify the unit and rate for the item");
      return { status: false, content };
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- dead by design; see the note above.
    if (!itemCurrentDetails.code && itemCurrentDetails.purchaseUnit && itemCurrentDetails.rate === null) {
      content = t("Please enter the value of rate");
      return { status: false, content };
    }

    if (!itemCurrentDetails.code && !itemCurrentDetails.purchaseUnit && itemCurrentDetails.rate) {
      content = t("Please enter the value of unit");
      return { status: false, content };
    }

    if (!itemCurrentDetails.description) {
      content = t("Please enter the description for the item");
      return { status: false, content };
    }

    if (!itemCurrentDetails.code && rateValue < 0) {
      content = t("Please enter a positive value for rate");
      return { status: false, content };
    }

    if (!itemCurrentDetails.hasChild && itemCurrentDetails.purchaseUnit) {
      if (Number.isNaN(conversionRateMU) || conversionRateMU <= 0) {
        content = t("Please enter a positive value for relation");
        return { status: false, content };
      }

      if (Number.isNaN(conversionRatePU) || conversionRatePU <= 0) {
        content = t("Please enter a positive value for relation");
        return { status: false, content };
      }
    }

    return { status: true, content: undefined };
  };

  const generatePayload = () => {
    const additionalAmountValue = Number(itemCurrentDetails.additionalAmount);
    let data: NonNullable<GetEstimateItemsRequestValue> = {
      additionalAmount: Number.isNaN(additionalAmountValue) ? null : additionalAmountValue,
      itemId: itemCurrentDetails.itemId,
    };

    if (!itemCurrentDetails.hasChild) {
      data = {
        ...data,
        quantity: itemCurrentDetails.manualQuantity,
        measurementUnit: itemCurrentDetails.measurementUnit,
      };
      if (itemCurrentDetails.purchaseUnit) {
        const conversionRatePUValue = Number(itemCurrentDetails.conversionRatePU);
        const conversionRateMUValue = Number(itemCurrentDetails.conversionRateMU);
        const conversionRatePU = Number.isNaN(conversionRatePUValue) ? 1 : conversionRatePUValue;
        const conversionRateMU = Number.isNaN(conversionRateMUValue) ? 1 : conversionRateMUValue;
        data.conversionRate = `${conversionRatePU}:${conversionRateMU}`;
      }
    }

    if (!itemCurrentDetails.code) {
      data.description = itemCurrentDetails.description;
      if (!itemCurrentDetails.hasChild) {
        data.rate = itemCurrentDetails.rate;

        if (addonsAccess.allowed) {
          data = {
            ...data,
            rateAnalysis: itemCurrentDetails.isRateAnalysisEnable ? itemCurrentDetails.rateAnalysis : null,
            isRateAnalysisEnable: itemCurrentDetails.isRateAnalysisEnable,
          };
        }

        if (!Number.isFinite(itemCurrentDetails.quantityFromTakeoffs)) {
          data.purchaseUnit = itemCurrentDetails.purchaseUnit;
        }
      }
    }

    return {
      value: data,
      params: {
        estimateId: params.estimateId,
      },
      action: "update" as const,
    };
  };

  const saveEstimateItemDetails = async () => {
    setButtonLoading(true);

    const validation = validateItemDetails();
    if (!validation.status && validation.content) {
      dispatch(showToastNotification({ content: validation.content, type: TOAST_TYPE_ERROR }));
      setButtonLoading(false);
      return;
    }
    const payload = generatePayload();
    const response = await saveItemInEstimate(payload);
    if (response.data.status) {
      dispatch(showToastNotification({ content: t("Item has been saved successfully"), type: TOAST_TYPE_SUCCESS }));
      if (isSavedBoqItem(response.data.result)) {
        refreshCallBack(response.data.result);
      }
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setButtonLoading(false);
  };

  const fetchTakeoffsList = useCallback(async () => {
    if (!itemCurrentDetails.itemId || !params.estimateId) return;
    const response = await fetchTakeoffsListAttachedToItem({
      itemId: itemCurrentDetails.itemId,
      versionId: params.versionId,
      estimateId: params.estimateId,
    });

    if (response.data.status && response.data.result) {
      setAttachedTakeoffsList(response.data.result);
    }
  }, [itemCurrentDetails.itemId, params.estimateId, params.versionId]);

  useEffect(() => {
    if (!itemCurrentDetails.hasChild) {
      fetchTakeoffsList();
    }
  }, [fetchTakeoffsList, itemCurrentDetails.hasChild]);

  // ADDED note: MeasurementUnitOption is declared above the component to be visible
  // in the `useState` generic for `measurementUnitsDataSource`.
  const refactorUnitsData = (data: MeasurementUnitOption[]) => {
    const unitsWithTypeMap: {
      [k: string]: Array<{ _id: string; name: string; type: MeasurementUnitOption["type"] }>;
    } = {};
    data.forEach((unit: MeasurementUnitOption) => {
      const unitData = {
        _id: unit._id,
        name: unit.nameWithSymbol,
        type: unit.type,
      };
      const bucket = unitsWithTypeMap[unit.type.name] ?? [];
      if (!unitsWithTypeMap[unit.type.name]) {
        unitsWithTypeMap[unit.type.name] = bucket;
      }
      bucket.push(unitData);
    });

    return Object.keys(unitsWithTypeMap)
      .map((typeName: string) => {
        const firstUnit = unitsWithTypeMap[typeName]?.[0];
        if (!firstUnit) {
          return null;
        }
        return {
          _id: firstUnit.type.id,
          name: typeName,
          selectable: false,
          units: unitsWithTypeMap[typeName] ?? [],
        };
      })
      .filter((item): item is MeasurementUnitTypeGroup => Boolean(item));
  };

  const getUnits = async () => {
    const response = await getMeasurementUnits({});
    if (response.data.status) {
      setMeasurementUnitsDataSource(refactorUnitsData((response.data.result ?? []).filter(isMeasurementUnitOption)));
    } else {
      dispatch(showToastNotification({ content: t("Unable to fetch Measurment Units"), type: TOAST_TYPE_ERROR }));
      closeEditModalPopUp();
    }
  };

  useEffect(() => {
    getUnits();
  }, []);

  // `rate` and `manualQuantity` are cleared to `null` (not `undefined`), so the parameter defaults
  // below never fire for a cleared field — hence the explicit normalisation.
  const getLatestAmount = (rate: number | null = 0, manualQuantity: number | null = 0, quantityFromTakeoffs = 0) => {
    return Number(rate) * ((manualQuantity ?? 0) + quantityFromTakeoffs);
  };

  // const generateQuantityFromTakeoffs = (currentData)=>{
  //   if(_.get(itemDetails, "measurementUnit.relation")){
  //     const intialConversionRatePU = parseFloat(itemDetails.measurementUnit.relation.split(":")[0]) || 1
  //     const intialConversionRateMU = parseFloat(itemDetails.measurementUnit.relation.split(":")[1]) || 1

  //     let currentConversionRatePU = parseFloat(currentData.conversionRatePU) || 1
  //     let currentConversionRateMU = parseFloat(currentData.conversionRateMU) || 1

  //     return ((currentData.quantityFromTakeoffs) *(currentConversionRateMU/currentConversionRatePU) *(intialConversionRatePU/intialConversionRateMU))
  //   }else{
  //     return currentData.quantityFromTakeoffs
  //   }
  // }

  type UpdateArgs =
    | {
        target: {
          value: string;
          itemData?: {
            name?: string;
            defaultMeasurementUnitId?: string;
            defaultMeasurementUnit?: string;
            defaultConversionRate?: string;
          };
          name?: string;
        };
      }
    /**
     * The measurement-unit tree hands back its own node — `{ id, label, parentId }` built from the
     * unit's `_id` by `flattenMeasurementUnitsForTree` in BoqItemUnitDetails — so the id arrives
     * UNBRANDED. `_id` is declared because the picker types the row as the unit document merged
     * with the node, but only the node's own keys are actually present at runtime.
     */
    | { itemData: { id?: string; _id?: ObjectIdString } }
    | RateAnalysisPayload;

  // ADDED: updateItemDetails accepts a polymorphic `args` from multiple callers
  // (form input change, Cost Database row select, rate analysis save). The narrow guards
  // below keep each branch type-safe without introducing a wider input shape.
  // The last three members are the payloads the tab children declare that the first four cannot
  // express; each used to be forced through an `as` at the child's `updateItemDetails` prop.
  type UpdateItemDetailsInput =
    | Extract<UpdateArgs, { target: unknown }>
    | Extract<UpdateArgs, { itemData: unknown }>
    | RateAnalysisPayload
    | { value: { _id?: string; name?: string } & Partial<MeasurementUnitOption> }
    /** QUANTITY tab: same numeric-box payload, but that tab declares `target` optional. */
    | {
        value: number | null;
        previousValue?: number | null;
        event?: React.ChangeEvent<HTMLInputElement>;
        target?: { value: string; name?: string };
      }
    /** UNIT tab: the measurement-unit tree sends the picked node plus its id. */
    | { itemData?: MeasurementUnitListRow; value: string[] }
    /** RATE tab: the rate-analysis toggle; the branch below re-reads `prev`, not the payload. */
    | { checked: boolean };
  const updateItemDetails = (args: UpdateItemDetailsInput, field?: string, checkNumberIsPositive = false) => {
    const hasTarget = (value: UpdateItemDetailsInput): value is Extract<UpdateArgs, { target: unknown }> =>
      typeof value === "object" && "target" in value;
    const hasItemData = (value: UpdateItemDetailsInput): value is Extract<UpdateArgs, { itemData: unknown }> =>
      typeof value === "object" && "itemData" in value;
    const hasRateAnalysisData = (value: UpdateItemDetailsInput): value is RateAnalysisPayload =>
      typeof value === "object" && "rateAnalysisData" in value;
    /** The purchase-unit AutoComplete sends the picked row (or nothing, when it is cleared) under `value`. */
    const hasUnitValue = (
      value: UpdateItemDetailsInput,
    ): value is { value: { _id?: string; name?: string } & Partial<MeasurementUnitOption> } =>
      "value" in value && typeof value.value === "object" && value.value !== null && !Array.isArray(value.value);
    if (field && ["rate", "manualQuantity"].includes(field)) {
      const target = hasTarget(args) ? args.target : { value: "" };
      let fieldValue = Number(target.value);
      if (Number.isNaN(fieldValue) || (checkNumberIsPositive && fieldValue < 0)) {
        target.value = "";
        fieldValue = NaN;
      }
      setItemCurrentDetails((prev) => {
        const next = { ...prev, [field]: Number.isNaN(fieldValue) ? null : fieldValue };
        return { ...next, amount: getLatestAmount(next.rate, next.manualQuantity, next.quantityFromTakeoffs) };
      });
    } else if (field === "purchaseUnit") {
      setItemCurrentDetails((prev) => {
        const unit = hasUnitValue(args) ? args.value : undefined;
        if (unit) {
          prev = { ...prev, [field]: unit._id, purchaseUnitName: unit.name };
          if (!prev.measurementUnit) {
            prev = {
              ...prev,
              measurementUnit: unit.defaultMeasurementUnitId,
              measurementUnitName: unit.defaultMeasurementUnit,
              conversionRatePU: unit.defaultConversionRate?.split(":")[0],
              conversionRateMU: unit.defaultConversionRate?.split(":")[1],
            };
          }
        }

        return prev;
      });
    } else if (field == "measurementUnit") {
      // `measurementUnit` is a branded `objectIdString`, but the tree node carries the unit id as a
      // plain string (see `UpdateArgs` above), so it is PARSED rather than asserted into the brand.
      const itemData = hasItemData(args) ? args.itemData : undefined;
      const measurementUnitId = itemData?._id ?? objectIdString.safeParse(itemData?.id).data;
      setItemCurrentDetails((prev) => ({ ...prev, [field]: measurementUnitId }));
    } else if (field === "isRateAnalysisEnable") {
      setItemCurrentDetails((prev) => {
        const shouldEnable = !prev.isRateAnalysisEnable;

        return {
          ...prev,
          rate: shouldEnable ? prev.rateAnalysis.rateAnalysisData.say : prev.rate,
          [field]: shouldEnable,
        };
      });
    } else if (field === "rateAnalysis") {
      const rateAnalysisArgs: RateAnalysisPayload = hasRateAnalysisData(args)
        ? args
        : { rateAnalysisData: { say: 0, total: 0, data: [] }, purchaseUnitCount: 1 };
      setItemCurrentDetails((prev) => ({
        ...prev,
        rateAnalysis: {
          ...prev.rateAnalysis,
          rateAnalysisData: {
            ...prev.rateAnalysis.rateAnalysisData,
            ...rateAnalysisArgs.rateAnalysisData,
            data: rateAnalysisArgs.rateAnalysisData.data.map((row: RateAnalysisRow) => ({
              ...row,
              id: row.id ?? null,
            })),
          },
          purchaseUnitCount: rateAnalysisArgs.purchaseUnitCount,
        },
        rate: rateAnalysisArgs.rateAnalysisData.say,
      }));
    } else {
      const target = hasTarget(args) ? args.target : { value: "", name: "" };
      let fieldValue = target.value;
      if (checkNumberIsPositive) {
        const numericValue = Number(fieldValue);
        if (!Number.isNaN(numericValue) && numericValue < 0) {
          fieldValue = "";
          target.value = "";
        }
      }
      const fieldName = field || target.name;
      if (fieldName) {
        setItemCurrentDetails((prev) => ({ ...prev, [fieldName]: fieldValue }));
      }
    }
  };

  const selectedTabContent = () => {
    // `updateItemDetails` is a polymorphic dispatcher: `UpdateItemDetailsInput` above is the union
    // of every payload the tab children declare, so each child's own (narrower) parameter type
    // accepts it directly — no per-boundary cast.
    const commonPropsBase = {
      itemCurrentDetails,
      currencyDetails,
      isActionPermission,
      attachedTakeoffsList,
    };
    if (currentTab == EditBOQItemsTabs.DETAILS.value) {
      return (
        <BoqItemBasicDetails {...commonPropsBase} updateItemDetails={updateItemDetails} setCurrentTab={setCurrentTab} />
      );
    } else if (currentTab == EditBOQItemsTabs.QUANTITY.value) {
      return <BoqItemQuantityDetails {...commonPropsBase} updateItemDetails={updateItemDetails} />;
    } else if (currentTab == EditBOQItemsTabs.UNIT.value) {
      return (
        <BoqItemUnitDetails
          {...commonPropsBase}
          updateItemDetails={updateItemDetails}
          measurementUnitsDataSource={measurementUnitsDataSource}
        />
      );
    } else if (currentTab == EditBOQItemsTabs.RATE.value) {
      return <BoqItemRateDetails {...commonPropsBase} updateItemDetails={updateItemDetails} />;
    } else if (currentTab == EditBOQItemsTabs.AMOUNT.value) {
      return <BoqItemAmountDetails {...commonPropsBase} updateItemDetails={updateItemDetails} />;
    }
  };

  const content = () => (
    <div className="h-full flex flex-col">
      <div className="px-8 border-0 border-b border-button_light">
        <Box sx={{ paddingBottom: "0.1875rem" }}>
          <Tabs
            value={currentTab}
            onChange={(event, selectedTab) => setCurrentTab(selectedTab)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#6BBBE3",
                height: "0.25rem",
                borderRadius: "1.25rem",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "1rem",
                color: "#192048",
                minWidth: "auto",
                marginInlineEnd: "3rem",
                padding: 0,
              },
              "& .Mui-selected": {
                color: "#192048",
                fontWeight: 600,
              },
            }}
          >
            {EDIT_BOQ_TAB_KEYS.map((tabKey) => (
              <Tab label={EditBOQItemsTabs[tabKey].text} value={tabKey} key={tabKey} />
            ))}
          </Tabs>
        </Box>
      </div>
      <div className="px-8 flex w-full h-full border-0 border-b border-button_light">{selectedTabContent()}</div>
    </div>
  );

  const footer = () => (
    <div className="flex gap-4 px-8 py-3">
      <ButtonComponent
        onClick={saveEstimateItemDetails}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        loading={buttonLoading}
      >
        {t("Confirm")}
      </ButtonComponent>
      <ButtonComponent
        onClick={closeEditModalPopUp}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        loading={buttonLoading}
      >
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      footerContent={footer()}
      headerTitle={<span className="px-8 py-3">{t("Edit Item")}</span>}
      isVisible
      onCancel={closeEditModalPopUp}
      modelContent={content()}
      width="80%"
      height="95%"
      modalPadding={0}
    />
  );
};

export default BoqItemEditPopUp;
